// Ad Preview Mockups Stage 1 §13.7. Pure helper — builds the variants
// rendered by GoogleSearchMockup, respecting Google RSA pin positions.
//
// Real Google Ads behaviour:
//   - A headline pinned to position N can ONLY appear in slot N
//   - An unpinned headline can appear in any slot that has no
//     headlines pinned to it
//   - When a slot HAS pinned headlines, ONLY those can show there
//     (unpinned headlines are excluded from that slot)
//
// Worked examples:
//
//   Pool: [{H1, pin: 1}, {H2, pin: 1}, {H3}, {H4}, {H5}]
//     Slot 1 → rotate H1, H2 (only the two pinned)
//     Slot 2 → rotate H3, H4, H5 (no pin → unpinned pool)
//     Slot 3 → rotate H3, H4, H5
//
//   Pool: [{H1}, {H2}, {H3}]   (no pins)
//     Slot 1 → rotate H1, H2, H3
//     Slot 2 → rotate H1, H2, H3
//     Slot 3 → rotate H1, H2, H3
//
//   Pool: [{H1, pin: 1}, {H2, pin: 2}, {H3, pin: 3}]
//     Slot 1 → H1 (only candidate)
//     Slot 2 → H2
//     Slot 3 → H3
//
//   Pool: [{H1, pin: 1}]   (only slot 1 has anything pinnable; no
//                          unpinned to fall back on for slots 2/3)
//     Slot 1 → H1
//     Slot 2 → null
//     Slot 3 → null
//
// Rotation: variant index drives which candidate each slot picks,
// modulo the slot's pool size. So variant 0 picks pool[0] in each
// slot, variant 1 picks pool[1 % size], etc. This gives the
// franchisee visible variety across the rendered 3-5 cards without
// trying to enumerate the full Cartesian product (which can blow up).

import type { GoogleHeadline } from '../types/campaign.js';

export type GoogleAdVariant = {
  // [slot1, slot2, slot3]. Null = pool empty (no pinned candidate
  // AND no unpinned fallback). The mockup component should omit
  // empty slots rather than render a blank "Title -" stub.
  headlines: [string, string | null, string | null];
  description: string;
};

const MIN_VARIANTS = 1;
const MAX_VARIANTS = 5;

// Group input headlines into pinned and unpinned pools. Whitespace-
// only entries are dropped at the call site so we don't propagate
// empty strings into rendered slots.
function partition(
  headlines: readonly GoogleHeadline[],
): { pinned: Record<1 | 2 | 3, string[]>; unpinned: string[] } {
  const pinned: Record<1 | 2 | 3, string[]> = { 1: [], 2: [], 3: [] };
  const unpinned: string[] = [];
  for (const h of headlines) {
    const text = h.text.trim();
    if (text.length === 0) continue;
    if (h.pinPosition === 1 || h.pinPosition === 2 || h.pinPosition === 3) {
      pinned[h.pinPosition].push(text);
    } else {
      unpinned.push(text);
    }
  }
  return { pinned, unpinned };
}

// For a given slot, return the pool the slot draws from:
// - If any headlines are pinned to this slot, that's the pool
//   (unpinned headlines are EXCLUDED — real Google won't surface
//   them in a slot that has pinned candidates).
// - Otherwise the pool is the unpinned headlines.
// - Empty array means "nothing to render here".
function slotPool(
  slot: 1 | 2 | 3,
  pools: ReturnType<typeof partition>,
): string[] {
  const pinned = pools.pinned[slot];
  if (pinned.length > 0) return pinned;
  return pools.unpinned;
}

// Stage 1 §13.10. Within a single variant, the three slots must show
// different headlines — otherwise the same unpinned headline would
// appear in slots 2 AND 3 ("Get Fit · Get Fit · Get Fit"). Exclude
// the values already chosen for earlier slots in this same variant.
// Doesn't affect pinned-only pools: real Google would happily show
// the same headline pinned to two slots since the AM authored that
// configuration deliberately, but as a polish call we keep slot
// uniqueness regardless. AMs who genuinely want a headline twice can
// duplicate it in the Campaign's headline list.
function slotPoolExcluding(
  slot: 1 | 2 | 3,
  pools: ReturnType<typeof partition>,
  alreadyChosen: ReadonlySet<string>,
): string[] {
  return slotPool(slot, pools).filter((h) => !alreadyChosen.has(h));
}

// How many variants to render — bounded by [MIN, MAX], soft-capped
// at the largest distinct pool (no point in 5 identical cards when
// every slot has only 1 candidate).
function chooseVariantCount(
  pools: ReturnType<typeof partition>,
  descriptions: readonly string[],
): number {
  const slot1 = slotPool(1, pools).length;
  const slot2 = slotPool(2, pools).length;
  const slot3 = slotPool(3, pools).length;
  const distinct = Math.max(slot1, slot2, slot3, descriptions.length, 1);
  return Math.min(MAX_VARIANTS, Math.max(MIN_VARIANTS, distinct));
}

export function buildGoogleAdVariants(
  headlines: readonly GoogleHeadline[],
  descriptionsInput: readonly string[],
): GoogleAdVariant[] {
  const pools = partition(headlines);
  const descriptions = descriptionsInput
    .map((d) => d.trim())
    .filter((d) => d.length > 0);

  // Nothing to show. Caller handles the empty state.
  const anyHeadline =
    pools.pinned[1].length +
      pools.pinned[2].length +
      pools.pinned[3].length +
      pools.unpinned.length >
    0;
  if (!anyHeadline && descriptions.length === 0) return [];

  const count = chooseVariantCount(pools, descriptions);

  return Array.from({ length: count }, (_, i) => {
    // Stage 1 §13.10. Build the three slots in order, excluding
    // earlier-slot picks so the same headline can't repeat within
    // one variant. Across DIFFERENT variants the same headline can
    // still appear in the same slot — that's the rotation working
    // as designed.
    const chosen = new Set<string>();
    const s1 = pickFromPool(slotPoolExcluding(1, pools, chosen), i);
    if (s1) chosen.add(s1);
    const s2 = pickFromPool(slotPoolExcluding(2, pools, chosen), i);
    if (s2) chosen.add(s2);
    const s3 = pickFromPool(slotPoolExcluding(3, pools, chosen), i);
    // Slot 1 falls back to "(no headline)" when there are NO
    // headlines at all (every pool empty). In every other case slot 1
    // always picks something because pickFromPool returns the first
    // candidate for variant 0 and slot 1 has no exclusions applied.
    const slot1: string = s1 ?? '(no headline)';
    const description = descriptions[i % Math.max(descriptions.length, 1)] ?? '';
    return {
      headlines: [slot1, s2, s3] as [string, string | null, string | null],
      description,
    };
  });
}

function pickFromPool(pool: string[], variantIndex: number): string | null {
  if (pool.length === 0) return null;
  return pool[variantIndex % pool.length] ?? null;
}
